import { useEffect, useState, useCallback } from "react";
import { Search, Loader2, Save } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminTitle, Modal, Field } from "@/pages/admin/AdminUI";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

const STATUSES = ["Pending Payment", "Payment Detected", "Payment Confirmed", "Processing", "In Progress", "Completed", "Cancelled", "Refunded"];

export default function AdminOrders() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ status: "", internal_notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/orders", { params: { status: status || undefined, search: search || undefined } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [status, search]);
  useEffect(() => { load(); }, [load]);

  const openOrder = (o) => { setSel(o); setForm({ status: o.status, internal_notes: o.internal_notes || "" }); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/orders/${sel.order_id}`, form); toast.success("Order updated"); setSel(null); load(); }
    catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  return (
    <div>
      <AdminTitle title="Orders" />
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <input placeholder="Search order ID, name, email…" className="input-lux !pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-order-search" />
        </div>
        <select className="input-lux w-52" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-order-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead className="bg-black/30 text-slate-500 text-xs uppercase"><tr><th className="text-left px-5 py-3">Order</th><th className="text-left px-5 py-3">Customer</th><th className="text-left px-5 py-3">Service</th><th className="text-left px-5 py-3">Total</th><th className="text-left px-5 py-3">Status</th></tr></thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} onClick={() => openOrder(o)} className="border-t border-white/5 cursor-pointer hover:bg-white/5" data-testid={`admin-order-row-${o.order_id}`}>
                  <td className="px-5 py-3 font-mono text-xs text-slate-400">{o.order_id}</td>
                  <td className="px-5 py-3 text-white">{o.user_name}<div className="text-xs text-slate-500">{o.user_email}</div></td>
                  <td className="px-5 py-3 text-slate-300">{o.service_name}</td>
                  <td className="px-5 py-3 text-white">{o.total} USDT</td>
                  <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No orders found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={!!sel} onClose={() => setSel(null)} title={`Order ${sel?.order_id}`}>
        {sel && (
          <div className="space-y-4">
            <div className="text-sm text-slate-400 space-y-1">
              <div>Customer: <span className="text-white">{sel.user_name}</span> ({sel.user_email})</div>
              <div>Service: <span className="text-white">{sel.service_name}{sel.package_name ? ` · ${sel.package_name}` : ""}</span></div>
              <div>Total: <span className="text-white">{sel.total} USDT</span> · Network: {sel.network}</div>
              {sel.customer_notes && <div>Customer notes: <span className="text-slate-300">{sel.customer_notes}</span></div>}
            </div>
            <Field label="Order Status">
              <select className="input-lux" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="admin-order-status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Internal Notes (not visible to customer)">
              <textarea rows={3} className="input-lux resize-none" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} data-testid="admin-order-notes" />
            </Field>
            <button onClick={save} disabled={saving} className="gold-btn w-full" data-testid="admin-order-save"><Save size={16} /> {saving ? "Saving…" : "Save Changes"}</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
